import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Plus, X } from '../../../../../components/icon/solar-icons.js'
import { PortalConfirmDialog } from '../../../../../components/portal-confirm-dialog/portal-confirm-dialog.js'

export interface CustomFieldDef {
    id: number
    name: string
    field_key: string
    field_type: string
    options: string | null
    visibility: string
}

export interface CustomFieldsSectionProps {
    lang: Language
    fields: CustomFieldDef[]
    slideOpen: boolean
    editingField: CustomFieldDef | null
    fieldForm: { name: string; field_type: string; options: string; visibility: string }
    saving: boolean
    onOpenCreate: () => void
    onOpenEdit: (field: CustomFieldDef) => void
    onClose: () => void
    onUpdateForm: (field: string, value: string) => void
    onSave: () => void
    onDelete: (id: number) => void
}

export const CustomFieldsSection = (props: CustomFieldsSectionProps) => {
    const { lang, fields, slideOpen, fieldForm, saving } = props
    const s = t.portal.settings.customFields
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)

    const typeLabel = (ft: string) => {
        const map: Record<string, () => string> = {
            text: () => s.typeText(lang),
            // 'longText' is the Humaans-imported spelling of the same type.
            textarea: () => s.typeTextarea(lang),
            longText: () => s.typeTextarea(lang),
            number: () => s.typeNumber(lang),
            date: () => s.typeDate(lang),
            select: () => s.typeSelect(lang),
        }
        return (map[ft] ?? map.text)()
    }

    const visLabel = (v: string) => {
        const map: Record<string, () => string> = {
            public: () => s.visPublic(lang),
            manager: () => s.visManager(lang),
            private: () => s.visPrivate(lang),
        }
        return (map[v] ?? map.public)()
    }

    return (
        <div>
            <div className="portal-settings__section-header">
                <h3 className="portal-settings__section-title">{s.title(lang)}</h3>
                <Button
                    variant="primary"
                    size="small"
                    iconLeft={Plus}
                    text={s.createField(lang)}
                    onClick={props.onOpenCreate}
                />
            </div>

            {fields.length === 0 ? (
                <div className="portal-settings__section-empty">{s.noFields(lang)}</div>
            ) : (
                <div className="portal-settings__data-list">
                    {fields.map((f) => (
                        <div key={f.id} className="portal-settings__data-row">
                            <div className="portal-settings__data-info">
                                <div className="portal-settings__data-name">{f.name}</div>
                                <div className="portal-settings__data-meta">
                                    {f.field_key} · {typeLabel(f.field_type)} ·{' '}
                                    {visLabel(f.visibility)}
                                    {f.options ? ` · ${f.options}` : ''}
                                </div>
                            </div>
                            <div className="portal-settings__data-actions">
                                <button type="button" onClick={() => props.onOpenEdit(f)}>
                                    {s.editField(lang)}
                                </button>
                                <button
                                    type="button"
                                    className="is-danger"
                                    onClick={() => setPendingDeleteId(f.id)}
                                >
                                    {s.deleteField(lang)}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <PortalConfirmDialog
                open={pendingDeleteId !== null}
                title={t.portal.shared.confirmTitle(lang)}
                message={s.confirmDelete(lang)}
                confirmLabel={t.portal.shared.confirmDelete(lang)}
                cancelLabel={t.portal.shared.confirmCancel(lang)}
                variant="danger"
                onConfirm={() => {
                    if (pendingDeleteId !== null) props.onDelete(pendingDeleteId)
                    setPendingDeleteId(null)
                }}
                onCancel={() => setPendingDeleteId(null)}
            />

            {slideOpen ? (
                <>
                    <div className="portal-slide-overlay" onClick={props.onClose} />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">
                                {props.editingField ? s.editField(lang) : s.createField(lang)}
                            </h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={props.onClose}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.fieldName(lang)}
                                    value={fieldForm.name}
                                    onChange={(value) => props.onUpdateForm('name', value)}
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <Select
                                    label={s.fieldType(lang)}
                                    value={fieldForm.field_type}
                                    onChange={(value) => props.onUpdateForm('field_type', value)}
                                    options={[
                                        { value: 'text', label: s.typeText(lang) },
                                        { value: 'textarea', label: s.typeTextarea(lang) },
                                        { value: 'number', label: s.typeNumber(lang) },
                                        { value: 'date', label: s.typeDate(lang) },
                                        { value: 'select', label: s.typeSelect(lang) },
                                    ]}
                                />
                            </div>
                            {fieldForm.field_type === 'select' ? (
                                <div className="portal-slide-over__field">
                                    <TextField
                                        label={s.options(lang)}
                                        value={fieldForm.options}
                                        onChange={(value) => props.onUpdateForm('options', value)}
                                        placeholder={s.optionsHint(lang)}
                                    />
                                </div>
                            ) : null}
                            <div className="portal-slide-over__field">
                                <Select
                                    label={s.visibility(lang)}
                                    value={fieldForm.visibility}
                                    onChange={(value) => props.onUpdateForm('visibility', value)}
                                    options={[
                                        { value: 'public', label: s.visPublic(lang) },
                                        { value: 'manager', label: s.visManager(lang) },
                                        { value: 'private', label: s.visPrivate(lang) },
                                    ]}
                                />
                            </div>
                        </div>
                        <div className="portal-slide-over__footer">
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={props.onClose}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={saving ? s.saving(lang) : s.save(lang)}
                                isDisabled={saving || !fieldForm.name}
                                onClick={props.onSave}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}
